import $ from 'jquery';

import { Breakpoints } from '../constants';

export default class MediaWrapper {
  /**
   *
   * @param {import('../ThemeBase').default} theme
   * @param {string} sectionId
   * @param {import('theme-checker/dist/shopify').Media} media
   * @param {HTMLElement} target
   */
  constructor(theme, sectionId, media, target) {
    this.theme = theme;
    this.sectionId = sectionId;
    this.mediaId = media.id;
    this.media = media;
    this.target = target;
    this.renderedElement = null;
    this.loaded = false;
    this.active = false;
    this.plyrController = null;
    this.modelController = null;
    this.youtubeController = null;
    this.vimeoController = null;
    this.externalVideoHost = null;
  }

  load = () => {
    return new Promise((resolve) => {
      if (this.loaded) {
        resolve();
        return;
      }

      if (this.media.media_type === 'image') {
        this.on_load();
        resolve();
        return;
      }

      var template = /** @type {HTMLTemplateElement} */ (document.querySelector(`#media-template-${this.sectionId}-${this.media.id}`));

      if (!template) {
        this.on_load();
        resolve();
        return;
      }
      const fragment = document.importNode(template.content, true);
      while (this.target.firstChild) {
        this.target.removeChild(this.target.lastChild);
      }
      this.target.appendChild(fragment);
      this.renderedElement = this.target.lastElementChild;

      switch (this.media.media_type) {
        case 'model':
          window.Shopify.loadFeatures([
            {
              name: 'model-viewer-ui',
              version: '1.0',
            },
          ], () => {
            this.modelController = new window.Shopify.ModelViewerUI(this.renderedElement.querySelector('model-viewer'));

            // Don't interact with the rest of the page when model is not disabled
            this.modelController.viewer.parentElement.addEventListener('mouseup', (e) => {
              e.stopPropagation = function () { };
            }, {
              capture: true,
            });

            // Prevent activating the viewer right after it was paused when clicked in position of the button
            this.modelController.elements.controlButton.addEventListener('click', (e) => {
              e.stopImmediatePropagation();
            }, { capture: true });

            this.modelController.viewer.addEventListener('shopify_model_viewer_ui_toggle_pause', this.on_pause.bind(this));
            this.modelController.viewer.addEventListener('shopify_model_viewer_ui_toggle_play', this.on_play.bind(this));

            this.on_load();
            resolve();
          });
          break;
        case 'video':
          window.Shopify.loadFeatures([
            {
              name: 'video-ui',
              version: '1.0',
              onLoad: function () {
                var video = this.renderedElement.querySelector('video');
                var loop = video.parentNode.dataset.videoLoop === 'true';
                this.plyrController = new window.Shopify.Plyr(video, {
                  loop: {
                    active: loop,
                  },
                  focusOnPlay: false,
                });
                video.addEventListener('play', this.on_play.bind(this));
                video.addEventListener('pause', this.on_pause.bind(this));
                video.addEventListener('ended', this.on_pause.bind(this));

                this.on_load();
                resolve();
              }.bind(this),
            },
          ]);
          break;
        case 'external_video':
          var iframe = /** @type {HTMLIFrameElement} */ (this.renderedElement.querySelector('iframe'));
          var loop = (/** @type {HTMLElement} */ (iframe.parentNode)).dataset.videoLoop === 'true';

          if(iframe.src.indexOf('youtube.com') !== -1) {
            this.externalVideoHost = 'youtube';

            this.theme.loadYoutubeApi().then(() => {
              new window.YT.Player(iframe, {
                events: {
                  onReady: (event) => {
                    this.youtubeController = /** @type {import('../window').YoutubePlayer} */ (event.target);
                    this.on_load();
                    resolve();
                  },
                  onStateChange: (event) => {
                    if (event.data === window.YT.PlayerState.PLAYING) {
                      this.on_play();
                    } else if (event.data === window.YT.PlayerState.PAUSED) {
                      this.on_pause();
                    } else if (event.data === window.YT.PlayerState.ENDED) {
                      if (loop) {
                        this.youtubeController.playVideo();
                      } else {
                        this.on_pause();
                      }
                    }
                  },
                  onError: (event) => {
                    console.warn('Youtube video error', event, event.data);
                    this.on_pause();
                  },
                }
              });
            });

          } else if (iframe.src.indexOf('vimeo.com') !== -1) {
            this.externalVideoHost = 'vimeo';
            let self = this;

            this.theme.loadVimeoApi().then(() => {
              // @ts-ignore
              let player = new window.Vimeo.Player(iframe);

              player.ready().then(function() {
                self.vimeoController = player;
                self.on_load();
                resolve();
              });

              player.on('play', function() {
                self.vimeoController.setLoop(loop);
                self.on_play();
              });

              player.on('pause', function() {
                self.on_pause();
              });
            });
          }
          break;
        default:
          this.on_load();
          resolve();
      }
    });
  };

  isMobile = () => {
    return window.matchMedia('only screen and (max-width: 767px)').matches;
  }

  on_load = () => {
    this.loaded = true;
    this.target.querySelector('.product-medias__media').classList.add('product-medias__media--loaded');

    var e = new CustomEvent('media-load', {
      detail: {
        parent: this.target,
        media: this,
      },
    });

    this.target.dispatchEvent(e);

    if (window.ShopifyXR && window.ShopifyXR.setupXRElements) {
      window.ShopifyXR.setupXRElements();
    }
  }

  play = () => {
    if (!this.loaded) {
      this.load().then(this._play.bind(this));
    } else {
      this._play();
    }
  };

  _play = () => {
    switch (this.media.media_type) {
      case 'model':
        this.modelController.play();
        this.on_play();
        break;
      case 'video':
        this.plyrController.play().then(this.on_play.bind(this)).catch(console.warn);
        break;
      case 'external_video':
        if(this.externalVideoHost == 'youtube') {
          this.youtubeController.playVideo();
        }
        else if(this.externalVideoHost == 'vimeo') {
          this.vimeoController.play();
        }
        this.on_play();
        break;
    }
  };

  on_play = () => {
    this.active = true;
    this.target.querySelector('.product-medias__media').classList.add('product-medias__media--active');

    if (!this.loaded) {
      this.loaded = true;
      this.target.querySelector('.product-medias__media').classList.add('product-medias__media--loaded');
    }

    var e = new CustomEvent('media-play', {
      detail: {
        parent: this.target,
        media: this,
      },
    });

    this.target.dispatchEvent(e);

    if (document.activeElement === document.body) {
      // No element has focus, focus this media
      this.focus();
    }
  };

  autoplay = () => {
    if ($(window).width() > 767 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.play();
    }
  };

  pause = () => {
    if (!this.active) {
      return;
    }
    switch (this.media.media_type) {
      case 'model':
        break;
      case 'video':
        this.plyrController.pause();
        break;
      case 'external_video':
        if(this.externalVideoHost == 'youtube') {
          this.youtubeController.pauseVideo();
        }
        else if(this.externalVideoHost == 'vimeo') {
          this.vimeoController.pause();
        }
        break;

    }
    this.on_pause();
  };

  togglePlay = () => {
    if (this.active) {
      this.pause();
    } else {
      this.play();
    }
  }

  on_pause = () => {
    this.active = false;
    this.target.querySelector('.product-medias__media').classList.remove('product-medias__media--active');
    var e = new CustomEvent('media-pause', {
      detail: {
        parent: this.target,
        media: this,
      },
    });
    this.target.dispatchEvent(e);
  };

  focus = () => {
    switch (this.media.media_type) {
      case 'video':
        (/** @type {HTMLElement} */ (this.target.querySelector('.plyr'))).focus();
        break;
      case 'model':
        var viewer = this.target.querySelector('model-viewer');
        if (viewer && viewer.shadowRoot) {
          (/** @type {HTMLElement} */ (viewer)).focus();
        } else {
          // Not yet loaded, try later
          setTimeout(this.focus.bind(this), 1000);
        }
        break;
      case 'external_video':
        this.target.querySelector('iframe').contentWindow.focus();
        break;
      default:
        this.target.focus();
    }
  }

  destroy = () => {

  };

}
